import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/config';
import { Table, Button, Modal, Typography, Card, Row, Col, Input, Tag, Space, Avatar, Tooltip } from 'antd';
import { 
  TeamOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CloseCircleOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  BankOutlined,
  UndoOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import AcademiaRegister from '../../components/auth/AcademiaRegister';
import RejectReasonModal from '../../components/admin/RejectReasonModal';

const { Title, Text } = Typography;

const AcademiaManagement = () => {
  const [academia, setAcademia] = useState([]);
  const [filteredAcademia, setFilteredAcademia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAcademia, setEditingAcademia] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch academia from API
  const loadAcademia = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/academia');
      if (response.data && response.data.success) {
        setAcademia(response.data.academia);
        setFilteredAcademia(response.data.academia);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch academia');
      }
    } catch (error) {
      console.error('Error loading academia:', error);
      toast.error('Error loading academia. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAcademia();
  }, []);

  // Search Logic
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    const filtered = academia.filter((item) => 
      item.schoolName?.toLowerCase().includes(value) ||
      item.email?.toLowerCase().includes(value) ||
      item.phone?.includes(value) ||
      item.district?.toLowerCase().includes(value)
    );
    setFilteredAcademia(filtered);
  };

  const handleReset = () => {
    setSearchText('');
    setFilteredAcademia(academia);
  };

  // Approve / Reject registration status
  const handleApprove = (academiaId) => {
    Modal.confirm({
      title: 'Approve Academia Registration',
      content: 'Are you sure you want to approve this academia?',
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        try {
          const response = await api.put(`/admin/academia/${academiaId}/approve`);
          if (response.data && response.data.success) {
            toast.success('Academia approved successfully');
            loadAcademia();
          }
        } catch (error) {
          toast.error('Failed to approve academia');
        }
      }
    });
  };

  const handleRevert = (academiaId) => {
    Modal.confirm({
      title: 'Revert to Pending',
      content: 'This will move the record back to Pending review and it will be hidden from the public site if currently approved. Continue?',
      okText: 'Revert',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await api.put(`/admin/academia/${academiaId}/revert`);
          if (response.data && response.data.success) {
            toast.success('Academia reverted to pending');
            loadAcademia();
          }
        } catch (error) {
          toast.error('Failed to revert academia');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleReject = (academiaItem) => {
    setRejectTarget(academiaItem);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.put(`/admin/academia/${rejectTarget._id}/reject`, {
        reason: rejectReason
      });
      if (response.data && response.data.success) {
        toast.success('Academia rejected');
        setRejectModalOpen(false);
        loadAcademia();
      }
    } catch (error) {
      toast.error('Failed to reject academia');
    } finally {
      setActionLoading(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    setEditingAcademia(null);
    setIsModalOpen(true);
  };

  const handleEdit = (academiaItem) => {
    setEditingAcademia(academiaItem);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setTimeout(() => setEditingAcademia(null), 300);
  };

  const handleEditSuccess = () => {
    handleModalClose();
    loadAcademia();
  };

  // Delete Academia
  const handleDelete = (academiaId) => {
    Modal.confirm({
      title: 'Delete Academia',
      content: 'Are you sure you want to delete this academia? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/academia/${academiaId}`);
          
          if (response.data && response.data.success) {
            toast.success('Academia deleted successfully');
            loadAcademia();
          } else {
            throw new Error(response.data?.message || 'Failed to delete academia');
          }
        } catch (error) {
          toast.error('Failed to delete academia.');
        }
      }
    });
  };

  // Statistics Calculation
  const stats = {
    total: academia.length,
    active: academia.filter(a => a.isActive).length,
    inactive: academia.filter(a => !a.isActive).length,
    approved: academia.filter(a => a.status === 'approved').length
  };

  // Styles based on the screenshot
  const cardStyles = {
    total: { bg: '#e6f7ff', color: '#1890ff', icon: <BankOutlined /> },
    approved: { bg: '#f6ffed', color: '#52c41a', icon: <CheckCircleOutlined /> },
    active: { bg: '#fff7e6', color: '#fa8c16', icon: <CheckCircleOutlined /> },
    inactive: { bg: '#fff1f0', color: '#ff4d4f', icon: <CloseCircleOutlined /> }
  };

  // Table columns
  const columns = [
    {
      title: <span style={{ fontWeight: 500 }}>#</span>,
      key: 'index',
      width: 60,
      render: (text, record, index) => <Text style={{ color: '#666', fontSize:"16px" }}>{index + 1}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>School Name</span>,
      dataIndex: 'schoolName',
      key: 'schoolName',
      render: (text, record) => {
        // Construct proper image URL
        let imageUrl = '';
        if (record.logo) {
          if (record.logo.startsWith('http')) {
            imageUrl = record.logo;
          } else if (record.logo.startsWith('/')) {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${record.logo}`;
          } else {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${record.logo}`;
          }
        }
        
        return (
          <Space>
            {record.logo ? (
              <Avatar src={imageUrl} size={40} />
            ) : (
              <Avatar style={{ backgroundColor: '#3C007A' }} size={40}>
                {text?.charAt(0)?.toUpperCase()}
              </Avatar>
            )}
            <div>
              <Text strong style={{ color: '#333', fontSize:"15px", display: 'block' }}>{text}</Text>
              <Text type="secondary" style={{ fontSize:"12px" }}>{record.district || 'N/A'}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontWeight: 500 }}>Contact Info</span>,
      key: 'contact',
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize:"14px" }}>
            <MailOutlined style={{ marginRight: 5, color: '#1890ff' }} />
            {record.email}
          </Text>
          <Text style={{ fontSize:"14px" }}>
            <PhoneOutlined style={{ marginRight: 5, color: '#52c41a' }} />
            {record.phone}
          </Text>
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Principal</span>,
      dataIndex: 'principalName',
      key: 'principalName',
      render: (text) => <Text style={{ fontSize:"14px" }}>{text}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Status</span>,
      key: 'status',
      render: (record) => (
        <Space direction="vertical" size={4}>
          {record.status === 'approved' ? (
            <Tag color="success" icon={<CheckCircleOutlined />}>Approved</Tag>
          ) : record.status === 'pending' ? (
            <Tag color="warning" icon={<ClockCircleOutlined />}>Pending</Tag>
          ) : (
            <Tag color="error" icon={<CloseCircleOutlined />}>Rejected</Tag>
          )}
          {record.isActive ? (
            <Tag color="green">Active</Tag>
          ) : (
            <Tag color="red">Inactive</Tag>
          )}
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Created</span>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Text style={{ fontSize:"13px", color: '#666' }}>
          {moment(date).format('MMM DD, YYYY')}
        </Text>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Actions</span>,
      key: 'actions',
      width: 120,
      render: (record) => (
        <Space size="small">
          {record.status === 'pending' && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="link"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApprove(record._id)}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="link"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleReject(record)}
                />
              </Tooltip>
            </>
          )}
          {(record.status === 'approved' || record.status === 'rejected') && (
            <Tooltip title="Revert to Pending">
              <Button
                type="link"
                icon={<UndoOutlined />}
                onClick={() => handleRevert(record._id)}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px', fontFamily: "'Rubik', sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, color: '#333', fontFamily: "'Rubik', sans-serif" }}>
          Academia Management
        </Title>
        <Text type="secondary" style={{ fontFamily: "'Rubik', sans-serif" }}>
          Manage all academic institutions
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.total.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.total.color }}>{cardStyles.total.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.total.color, display: 'block' }}>
                  {stats.total}
                </Text>
                <Text type="secondary">Total Academia</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.approved.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.approved.color }}>{cardStyles.approved.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.approved.color, display: 'block' }}>
                  {stats.approved}
                </Text>
                <Text type="secondary">Approved</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.active.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.active.color }}>{cardStyles.active.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.active.color, display: 'block' }}>
                  {stats.active}
                </Text>
                <Text type="secondary">Active</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.inactive.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.inactive.color }}>{cardStyles.inactive.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.inactive.color, display: 'block' }}>
                  {stats.inactive}
                </Text>
                <Text type="secondary">Inactive</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Search and Filter */}
      <div style={{ 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '8px 8px 0 0', 
        marginBottom: '0px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '200px' }}>
          <Input 
            placeholder="Search academia by name, email, phone, or district"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            style={{ maxWidth: '400px', fontFamily: "'Rubik', sans-serif" }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Reset
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <Text type="secondary" className="hidden md:block">
            Showing {filteredAcademia.length} of {academia.length} records
          </Text>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={openAddModal}
            style={{ backgroundColor: '#1890ff', borderRadius: '4px' }}
          >
            Add Academia
          </Button>
        </div>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredAcademia}
        loading={loading}
        rowKey={(record) => record._id}
        pagination={{ 
          pageSize: 10, 
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items` 
        }}
        scroll={{ x: 'max-content' }}
        rowClassName="align-middle"
        style={{ background: '#fff', borderRadius: '0 0 8px 8px' }}
      />

      {/* Edit Modal */}
      <Modal
        title={editingAcademia ? 'Edit Academia' : 'Add New Academia'}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={1000}
        centered
        destroyOnClose
      >
        <AcademiaRegister
          editingAcademia={editingAcademia}
          isAdminEdit={true}
          onSuccess={handleEditSuccess}
        />
      </Modal>

      <RejectReasonModal
        open={rejectModalOpen}
        title="Reject Academia Registration"
        entityLabel={rejectTarget?.schoolName}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        onOk={submitRejection}
        onCancel={() => setRejectModalOpen(false)}
        confirmLoading={actionLoading}
      />
    </div>
  );
};

export default AcademiaManagement;
